import { create } from 'zustand';
import type { Client } from '@microsoft/microsoft-graph-client';
import { listFolder, readJson } from '@/lib/graph/onedrive-helpers';
import type { StateFile, StateHistoryEntry } from '@/types/conversation';

// Cache duration: 5 minutes
const CACHE_DURATION_MS = 5 * 60 * 1000;

export interface ParsedConversation {
  id: string;
  fileName: string;
  startedAt: string;
  turns: {
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
  }[];
}

/**
 * Parse a state file into a displayable conversation
 */
function parseStateFile(fileName: string, state: StateFile): ParsedConversation | null {
  if (!state.history || !Array.isArray(state.history)) {
    return null;
  }

  // Filter to user/assistant turns only
  const conversationTurns = state.history
    .filter((entry): entry is StateHistoryEntry => 
      entry.role === 'user' || entry.role === 'assistant'
    )
    .filter(entry => entry.content && entry.content.trim().length > 0)
    .map(entry => ({
      role: entry.role as 'user' | 'assistant',
      content: entry.content,
      timestamp: entry.meta?.at || new Date().toISOString(),
    }));

  if (conversationTurns.length === 0) {
    return null;
  }

  const startedAt = conversationTurns[0]?.timestamp || new Date().toISOString();

  return {
    id: fileName,
    fileName,
    startedAt,
    turns: conversationTurns,
  };
}

interface HistoryState {
  conversations: ParsedConversation[];
  isLoading: boolean;
  error: string | null;
  lastLoadedAt: number | null;

  // Actions
  loadHistory: (graph: Client, forceRefresh?: boolean) => Promise<void>;
  invalidateCache: () => void;
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  conversations: [],
  isLoading: false,
  error: null,
  lastLoadedAt: null,

  loadHistory: async (graph: Client, forceRefresh = false) => {
    const { conversations, lastLoadedAt, isLoading } = get();
    
    // Skip if already loading
    if (isLoading) return;
    
    // Check cache validity
    const now = Date.now();
    const isCacheValid = conversations.length >= 0 && lastLoadedAt && (now - lastLoadedAt < CACHE_DURATION_MS);
    
    if (isCacheValid && !forceRefresh) {
      return; // Use cached data
    }
    
    set({ isLoading: true, error: null });
    
    try {
      const files = await listFolder(graph, 'state');
      const jsonFiles = files.filter(f => f.name.endsWith('.json'));
      
      const convos: ParsedConversation[] = [];
      for (const file of jsonFiles.slice(0, 30)) { // Limit to 30 most recent
        try {
          const data = await readJson<StateFile>(graph, `state/${file.name}`);
          if (data) {
            const parsed = parseStateFile(file.name, data);
            if (parsed) {
              convos.push(parsed);
            }
          }
        } catch {
          // Skip invalid files
        }
      }
      
      // Sort by most recent
      convos.sort((a, b) => 
        new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
      );
      
      set({ conversations: convos, isLoading: false, lastLoadedAt: Date.now() });
    } catch (error) {
      console.error('Failed to load history:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load history',
        isLoading: false,
      });
    }
  },

  invalidateCache: () => {
    set({ lastLoadedAt: null });
  },
}));

